/**
 * tourSteps/createTaskSteps.js
 * Comprehensive guide for the Task & Milestone Creation Drawer.
 * Explains how to create a new task or milestone, all required fields,
 * item type switching, project assignment, owner accountability, deadlines,
 * completion criteria, deliverables checklists, and effort estimation.
 */

export const createTaskSteps = [
    {
        title: 'Work Item Type: Task vs Milestone',
        description: 'Decide what type of item you are creating. Choose "○ Task" for actionable daily work with active timers and subtasks. Choose "◆ Milestone" for high-level contractual phase gates, client sign-offs, or major project checkpoints.',
        selector: '[data-tour="task-item-type-switch"]',
        placement: 'bottomLeft',
    },
    {
        title: 'Title / Work Item Name (Required)',
        description: 'Enter a concise, descriptive title. For tasks, use action-oriented names (e.g. "Implement OAuth Authentication"). For milestones, name the phase deliverable (e.g. "Phase 1: Architecture & UI Sign-off").',
        selector: '[data-tour="task-title-input"]',
        placement: 'bottom',
    },
    {
        title: 'Primary Project Association (Required)',
        description: 'Every work item must belong to a parent project. If not pre-selected, click "+ Select Project" to assign one. You can also link secondary projects for cross-functional initiatives.',
        selector: '[data-tour="task-projects-section"]',
        fallbackSelector: '[data-tour="task-metadata-grid"]',
        placement: 'top',
    },
    {
        title: 'Assignee or Milestone Owner',
        description: 'Assign the responsible team member. For tasks, this is the individual executing the work. For milestones, this designates the official Milestone Owner accountable for deliverable sign-offs and CRE client synchronization.',
        selector: '[data-tour="task-assignee-select"]',
        fallbackSelector: '[data-tour="task-metadata-grid"]',
        placement: 'bottom',
    },
    {
        title: 'Schedule & Target Due Date',
        description: 'Set target start and completion deadlines. For tasks, this powers daily workload alerts and past-due notifications. For milestones, it establishes contractual delivery gates on the Gantt timeline.',
        selector: '[data-tour="task-due-date-select"]',
        fallbackSelector: '[data-tour="task-metadata-grid"]',
        placement: 'bottom',
    },
    {
        title: 'Execution Stage & Urgency Priority',
        description: 'Select the initial lifecycle stage (usually "Backlog" or "In Progress") and urgency priority (Critical, High, Medium, Low) to keep team backlogs properly prioritized.',
        selector: '[data-tour="task-priority-select"]',
        fallbackSelector: '[data-tour="task-status-select"]',
        placement: 'bottom',
    },
    {
        title: 'Effort Estimation Budget',
        description: 'Set estimated hours and minutes for this work item. The system automatically monitors time logged by employees against this budget and flags overrun warnings.',
        selector: '[data-tour="task-effort-estimate"]',
        fallbackSelector: '[data-tour="task-projects-section"]',
        placement: 'top',
    },
    {
        title: 'Parent Milestone Association (For Tasks)',
        description: 'When creating an operational Task, link it to an existing project Milestone. As team members complete this task, the milestone\'s overall phase progress bar dynamically advances towards 100%.',
        selector: '[data-tour="task-milestone-parent-select"]',
        fallbackSelector: '[data-tour="task-projects-section"]',
        placement: 'bottom',
    },
    {
        title: 'PM Completion Criteria (For Milestones)',
        description: 'When creating a Milestone, define formal acceptance criteria required for completion. Use the quick suggestion pills (e.g. "QA testing passed", "Client approval received") to quickly insert standard rules.',
        selector: '[data-tour="milestone-completion-criteria"]',
        fallbackSelector: '[data-tour="task-metadata-grid"]',
        placement: 'top',
    },
    {
        title: 'Mandatory Deliverables Checklist (For Milestones)',
        description: 'When creating a Milestone, list mandatory deliverables (e.g. Approved UI Prototype, API Documentation) that must be verified before the milestone can be marked complete.',
        selector: '[data-tour="milestone-deliverables-checklist"]',
        fallbackSelector: '[data-tour="task-metadata-grid"]',
        placement: 'top',
    },
    {
        title: 'Scope Description & File Attachments',
        description: 'Provide detailed requirements, acceptance criteria, or reference links. You can drag and drop images or paste files directly into the description box.',
        selector: '[data-tour="task-description-section"]',
        placement: 'top',
    },
    {
        title: 'Create & Publish Work Item',
        description: 'Click "Create Task" or "◆ Create Milestone" to validate required fields, save to the database, notify assignees, and publish live to the project dashboard!',
        selector: '[data-tour="create-task-submit-btn"]',
        placement: 'top',
    },
];
